import * as XLSX from 'xlsx';

export const fixBaselineResults = (data, isBaseline = false) => {
  if (!isBaseline) return data;

  const fixedData = {};
  Object.keys(data).forEach(model => {
    fixedData[model] = {};
    Object.keys(data[model]).forEach(language => {
      const values = data[model][language];
      fixedData[model][language] = values.map(value => {
        if (value > 100) return Math.min(value, 100);
        if (value < 0) return Math.max(value, 0);
        return Math.round(value * 100) / 100;
      });
    });
  });

  console.log(`Applied baseline fixes to method data`);
  return fixedData;
};

export const convertGoogleSheetsUrl = (url) => {
  const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (match) {
    return `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=xlsx`;
  }
  return null;
};

// Extract experiment name and setting from sheet name
// Format: "Experiment Name (setting)" where setting is monolingual, multilingual, or cross-lingual
// Also handles truncated names like "(mon", "(mul", "(cro"
export const parseSheetName = (sheetName) => {
  // First try to match full setting names
  let settingMatch = sheetName.match(/\((monolingual|multilingual|cross-lingual)\)/i);
  let setting = 'unknown';
  let experimentName = sheetName;

  if (settingMatch) {
    setting = settingMatch[1].toLowerCase();
    // Remove the setting part from the experiment name but keep the original format
    experimentName = sheetName.replace(/\s*\([^)]*\)\s*$/, '').trim();
  } else {
    // Try to match truncated/partial setting names (e.g., "(mon", "(mul", "(cro")
    const partialMatch = sheetName.match(/\((mon|mul|cro)[a-z-]*\)?$/i);
    if (partialMatch) {
      const partial = partialMatch[1].toLowerCase();
      if (partial === 'mon') setting = 'monolingual';
      else if (partial === 'mul') setting = 'multilingual';
      else if (partial === 'cro') setting = 'cross-lingual';
      // Remove the partial setting part from the experiment name
      experimentName = sheetName.replace(/\s*\([^)]*\)?\s*$/, '').trim();
    }
  }

  return {
    fullName: sheetName, // Keep original sheet name intact
    experimentName,
    setting
  };
};

export const parseExcelData = (workbook) => {
  const parsedData = {};
  const experimentsMetadata = {};
  const sheetNames = workbook.SheetNames;

  sheetNames.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length < 2) return;

    const headers = jsonData[0];
    const models = headers.slice(1);
    const methodResults = {};

    // Parse sheet name to extract experiment info
    const { fullName, experimentName, setting } = parseSheetName(sheetName);

    models.forEach(model => {
      if (model && model.trim()) {
        methodResults[model.trim()] = {
          'EN': [], 'ES': [], 'FR': [], 'IT': [], 'EU': [], 'KK': []
        };
      }
    });

    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (!row || row.length < 2) continue;

      const rowLabel = row[0];
      if (!rowLabel || typeof rowLabel !== 'string') continue;

      let language = '';
      if (rowLabel.includes('EN') || rowLabel.toLowerCase().includes('english')) language = 'EN';
      else if (rowLabel.includes('ES') || rowLabel.toLowerCase().includes('spanish')) language = 'ES';
      else if (rowLabel.includes('FR') || rowLabel.toLowerCase().includes('french')) language = 'FR';
      else if (rowLabel.includes('IT') || rowLabel.toLowerCase().includes('italian')) language = 'IT';
      else if (rowLabel.includes('EU') || rowLabel.toLowerCase().includes('basque')) language = 'EU';
      else if (rowLabel.includes('KK') || rowLabel.toLowerCase().includes('kazakh')) language = 'KK';

      if (!language) continue;

      models.forEach((model, modelIndex) => {
        if (model && model.trim() && methodResults[model.trim()]) {
          const value = parseFloat(row[modelIndex + 1]);
          if (!isNaN(value)) {
            methodResults[model.trim()][language].push(value);
          }
        }
      });
    }

    const isBaseline = sheetName.toLowerCase().includes('baseline');
    const fixedResults = fixBaselineResults(methodResults, isBaseline);

    if (Object.keys(fixedResults).length > 0) {
      // Store data using the full original sheet name as key
      parsedData[fullName] = fixedResults;
      // Store metadata for this experiment
      experimentsMetadata[fullName] = {
        fullName,
        experimentName,
        setting,
        models: Object.keys(fixedResults)
      };
    }
  });

  return { data: parsedData, metadata: experimentsMetadata };
};

// Get unique settings from metadata
export const getUniqueSettings = (metadata) => {
  const settings = new Set();
  Object.values(metadata).forEach(exp => {
    if (exp.setting !== 'unknown') {
      settings.add(exp.setting);
    }
  });
  return Array.from(settings);
};

// Get unique experiment names from metadata
export const getUniqueExperiments = (metadata) => {
  const experiments = new Set();
  Object.values(metadata).forEach(exp => {
    experiments.add(exp.experimentName);
  });
  return Array.from(experiments);
};

// Filter methods by settings
export const filterMethodsBySetting = (metadata, selectedSettings) => {
  if (!selectedSettings || selectedSettings.length === 0) {
    return Object.keys(metadata);
  }
  return Object.keys(metadata).filter(key => {
    const setting = metadata[key].setting;
    // Include methods whose setting matches selectedSettings, or methods with unknown setting
    return selectedSettings.includes(setting) || setting === 'unknown';
  });
};

// Filter methods by experiment name
export const filterMethodsByExperiment = (metadata, selectedExperiments) => {
  if (!selectedExperiments || selectedExperiments.length === 0) {
    return Object.keys(metadata);
  }
  return Object.keys(metadata).filter(key =>
    selectedExperiments.includes(metadata[key].experimentName)
  );
};